import { NOCS_PAGE_SIZE_DEFAULT, type NocItem } from "@seagames/contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { NocsTable } from "../../components/nocs/nocs-table";
import { nocListQuery } from "../../components/nocs/queries";
import { nocsPaging, nocsSearch } from "../../components/nocs/search";
import { PageHeader } from "../../components/page/page-header";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Participants › NOCs — the organizations entered in the current games, by
 * NOC code, with their athletes, entries and medals (`GET /nocs`, ONE
 * statement for the page). A games has tens, so the default page holds them
 * all; it is still paged, like every list.
 */
export const Route = createFileRoute("/_app/nocs/")({
	validateSearch: nocsSearch,
	component: NocsPage,
});

function NocsPage() {
	const { t } = useTranslation("nocs");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/nocs/" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;
	const { page, pageSize } = nocsPaging(search);

	const list = useQuery({
		...nocListQuery(gamesId ?? "", page, pageSize),
		enabled: gamesId !== null,
		placeholderData: keepPreviousData,
	});

	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) void navigate({ search: { ...search, page: pageToUrl(beyond) }, replace: true });
	}, [beyond]);

	const open = (n: NocItem) =>
		void navigate({ to: "/nocs/$nocId", params: { nocId: n.responsible_organization_id }, search: { games: gamesId ?? undefined } });

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />
			<NocsTable
				rows={list.data?.items ?? []}
				loading={list.isPending || (list.isFetching && list.isPlaceholderData)}
				error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
				empty={{ title: t("empty.noneTitle", { games: current.games.name }), description: t("empty.noneBody") }}
				toolbar={
					list.data ? (
						<span className="text-[0.8125rem] text-muted" aria-live="polite">
							{t("table.count", { count: total, n: formatInteger(total) })}
						</span>
					) : null
				}
				pagination={
					list.data && total > 0
						? {
								page,
								pageSize,
								total,
								onPageChange: (p) => void navigate({ search: { ...search, page: pageToUrl(p) } }),
								onPageSizeChange: (s) => void navigate({ search: { size: sizeToUrl(s, NOCS_PAGE_SIZE_DEFAULT), page: undefined } }),
								pageSizeOptions: PAGE_SIZE_CHOICES,
							}
						: undefined
				}
				onOpen={open}
			/>
		</div>
	);
}
